/**
 * IR node type definitions + constructors + runtime validator.
 *
 * This module is step 2 of the IR build order (see docs/IR_RULE_MAPPING.md §7).
 * It is the JS/JSDoc twin of docs/schemas/ir.schema.json — both MUST stay
 * in sync. The schema is the cross-layer contract the Java backend validates
 * against when it serializes to the wire; this file is how the frontend
 * constructs, inspects, and asserts IR trees at runtime.
 *
 * Design points (binding — see §2 IR sketch):
 *
 *  1. **Tagged discriminated union.** Every IRNode has a `kind` field whose
 *     value is one of {@link IR_KIND}. Predicates and layout switch on it.
 *
 *  2. **Invariants are enforced in constructors.** `makeLeaf()` stamps `w=350`;
 *     `makeTerminate()` stamps `h=90` and `hasIconBadge=false`; `makeSwitch()`
 *     stamps head `w=450 h=200`. Callers cannot accidentally violate these
 *     Conductor OSS-measured constants. See IR_RULE_MAPPING.md §3.1.
 *
 *  3. **Layout fields are optional at construction time.** `bbox`, `portMap`,
 *     `localTrunkX`, `subtreeWidth`, `measuredH` are populated by the layout
 *     pass (step 4). They're typed here for completeness.
 *
 *  4. **Sequence is implicit.** The IR does not have a `Sequence` node type
 *     as a distinct variant — a sequence is just an array of IRNode children
 *     inside a container (Switch.Case.children, Fork.Branch.children,
 *     DoWhile.body). `layoutSequence()` operates on arrays. This keeps the
 *     tree shape simpler and matches §2 of the mapping doc.
 *
 *  5. **Convergence is a child property, not a first-class node.** It lives
 *     on `Switch.convergence`, populated after layout when the living-branch
 *     set is known (Rule S-E1.c: pill-vs-label-marker choice).
 *
 *  6. **Join is a child property of Fork / DynamicFork.** Not a standalone
 *     variant — the JOIN belongs to its FORK, absorbed at IR-build time.
 *     This eliminates the "where does a JOIN live" scope question.
 *
 * See:
 *   - docs/IR_RULE_MAPPING.md §2 (IR sketch), §7 (build order)
 *   - docs/schemas/ir.schema.json (parallel contract)
 *   - frontend/src/utils/ir/kinds.js (IR_KIND / CANVAS_TYPE constants)
 */

import { IR_KIND, ALL_IR_KINDS, CANVAS_TYPE } from './kinds';

// ───────────────────────────────────────────────────────────────────
//  0. Shared sub-shape typedefs (JSDoc)
// ───────────────────────────────────────────────────────────────────

/**
 * Axis-aligned bounding box in the local coordinate space of the IR node's
 * parent. Populated by `layoutIR()` (step 4). Absent on freshly-constructed
 * nodes.
 *
 * @typedef {Object} BBox
 * @property {number} x      Top-left x (relative to parent)
 * @property {number} y      Top-left y (relative to parent)
 * @property {number} w      Width in px
 * @property {number} h      Height in px
 */

/**
 * Attach points for edges. Keys are semantic port names ("in", "out",
 * "true", "false", "default", "case:<name>", "loop_back"). Values are
 * {x, y} offsets within the node's bbox.
 *
 * @typedef {Object.<string, { x: number, y: number }>} PortMap
 */

/**
 * Arbitrary config payload pass-through. Retained verbatim from the source
 * canvas JSON so that nothing gets dropped on the render path. The IR
 * structure only looks at a handful of fields (e.g. `config.async` on
 * SubWorkflow); the rest flows through to the right panel.
 *
 * @typedef {Object.<string, unknown>} Props
 */

/**
 * A named case of a SWITCH. `children` is the sequence of IR nodes that
 * execute when this case matches. `isDefault` distinguishes the default
 * arm from the matched-value arms. `bbox` and `subtreeWidth` are populated
 * by layout.
 *
 * @typedef {Object} Case
 * @property {string} name                Case name (the match value, or "default")
 * @property {boolean} isDefault          True iff this is the SWITCH default arm
 * @property {IRNode[]} children          Sequence of nodes inside this case (possibly [])
 * @property {BBox} [bbox]                Post-layout bbox (absent at construction)
 * @property {number} [subtreeWidth]      Max subtreeWidth across children, for ordering
 */

/**
 * A named branch of a FORK_JOIN. Similar to Case but without the
 * default/match-value distinction.
 *
 * @typedef {Object} Branch
 * @property {string} name                Branch name (from the Conductor workflow)
 * @property {IRNode[]} children          Sequence of nodes inside this branch
 * @property {BBox} [bbox]                Post-layout bbox
 * @property {number} [subtreeWidth]      Max subtreeWidth for splay ordering
 */

/**
 * A FORK's explicit JOIN. Lives as a child property of `Fork` / `DynamicFork`.
 * Not a standalone IRNode variant — absorbed at IR-build time.
 *
 * @typedef {Object} Join
 * @property {string} id                  Stable unique id (usually the JOIN task's refName)
 * @property {string} label               Display label
 * @property {string[]} joinOn            Branch names this JOIN waits on
 * @property {'ALL' | 'ANY'} [waitMode]   All-of (default) vs any-of wait
 */

/**
 * The SWITCH convergence point. Can render as a catch pill (when ≥2 branches
 * are living) or as a label marker (when only one branch is living — Rule
 * S-E1.c). Populated by layout, not at construction.
 *
 * @typedef {Object} Convergence
 * @property {string} id
 * @property {'pill' | 'label_marker'} kind
 * @property {string[]} livingBranchRefs  Ids of branches contributing to convergence
 * @property {BBox} [bbox]
 */

// ───────────────────────────────────────────────────────────────────
//  1. IR node variants (discriminated union on `.kind`)
// ───────────────────────────────────────────────────────────────────

/**
 * Scalar task node. Every non-container, non-sentinel, non-terminate task
 * in the Conductor workflow becomes a Leaf.
 *
 * - `canvasType` is the canonical leaf sub-type (`http_task`, `script_task`,
 *   `task`, etc.) — kept verbatim from input so the renderer picks the
 *   right badge/widget.
 * - `measuredH` is the DOM-measured height, filled in after the first
 *   render pass and fed back into the next layout (Rule S-G.a: heights
 *   bucket to {100,110,120,130,140,150}). Absent before first measure.
 *
 * @typedef {Object} Leaf
 * @property {'leaf'} kind
 * @property {string} id
 * @property {string} label
 * @property {string} canvasType          One of CANVAS_TYPE leaf values
 * @property {Props} props                Pass-through config
 * @property {350} w                      Always 350 (Rule 0.SA.a)
 * @property {number} [measuredH]         DOM-measured height, filled post-render
 * @property {BBox} [bbox]
 * @property {PortMap} [portMap]
 */

/**
 * SWITCH / DECISION container.
 *
 * - Head is a 450×200 rounded rect (not diamond) — Rule 0.S2.a.
 * - `cases` are the match-value arms; `defaultCase` is optional.
 * - `convergence` is null until layout knows which branches are living.
 *
 * @typedef {Object} Switch
 * @property {'switch'} kind
 * @property {string} id
 * @property {string} label
 * @property {string} expr                The SWITCH expression (verbatim)
 * @property {Case[]} cases               Ordered; may be re-ordered after layout
 * @property {Case | null} defaultCase    Default arm (null if none)
 * @property {Convergence | null} convergence
 * @property {450} headW                  Always 450 (Rule 0.S2.a)
 * @property {200} headH                  Always 200 (Rule 0.S2.a)
 * @property {BBox} [bbox]                Full-subtree bbox (head + cases + convergence)
 * @property {PortMap} [portMap]
 * @property {number} [localTrunkX]       The SWITCH's own centroid (Rule §2 invariant 1)
 */

/**
 * Static FORK_JOIN container.
 *
 * - Head is 350×120 (Rule 0.SB.a).
 * - Symmetric ±278 splay between branches.
 * - `join` is the explicit JOIN pair; every static FORK has exactly one.
 *
 * @typedef {Object} Fork
 * @property {'fork'} kind
 * @property {string} id
 * @property {string} label
 * @property {Branch[]} branches
 * @property {Join} join
 * @property {350} headW
 * @property {120} headH
 * @property {BBox} [bbox]
 * @property {PortMap} [portMap]
 * @property {number} [localTrunkX]
 */

/**
 * Runtime-resolved parallel fan-out (FORK_JOIN_DYNAMIC).
 *
 * - `resolvedBranches` is null at design time; populated during execution view.
 * - At def time renders as a scalar-style fan-out indicator (350×120 head
 *   plus a single descendant indicating runtime expansion).
 *
 * @typedef {Object} DynamicFork
 * @property {'dynamicFork'} kind
 * @property {string} id
 * @property {string} label
 * @property {Branch[] | null} resolvedBranches  Null at design time
 * @property {Join} join
 * @property {350} headW
 * @property {120} headH
 * @property {BBox} [bbox]
 * @property {PortMap} [portMap]
 * @property {number} [localTrunkX]
 */

/**
 * DO_WHILE / FOR_EACH loop container.
 *
 * - Head is 350×130. Frame around body is 450×400+.
 * - `body` is the sequence inside the loop.
 * - `condition` is the loop-continue expression (e.g. `$.i < 10`).
 * - `iterationHint` is an optional display-only label like "foreach items".
 *
 * @typedef {Object} DoWhile
 * @property {'doWhile'} kind
 * @property {string} id
 * @property {string} label
 * @property {IRNode[]} body
 * @property {string} condition
 * @property {string} [iterationHint]
 * @property {350} headW
 * @property {130} headH
 * @property {BBox} [bbox]                Includes the 450×400+ frame around body
 * @property {PortMap} [portMap]
 * @property {number} [localTrunkX]
 */

/**
 * SUB_WORKFLOW (sync) or START_WORKFLOW (async).
 *
 * - `async=false` ⇒ sync call; canonical canvas type is `sub_workflow`.
 * - `async=true` ⇒ START_WORKFLOW; canvas type is still `sub_workflow`
 *   but with `async` flag on the flat node's config. The backend enum
 *   carve-out is documented in ConductorTaskType.java.
 * - `embedded` is the expanded sub-workflow IR, or null if not expanded.
 *
 * @typedef {Object} SubWorkflow
 * @property {'subWorkflow'} kind
 * @property {string} id
 * @property {string} label
 * @property {string} refName
 * @property {number} [refVersion]
 * @property {boolean} async
 * @property {IRNode | null} embedded     Expanded sub-workflow, or null
 * @property {350} headW
 * @property {130} headH
 * @property {BBox} [bbox]
 * @property {PortMap} [portMap]
 */

/**
 * TERMINATE / TERMINATE_WORKFLOW — dead-end node.
 *
 * - 350×90, no icon badge (Rule 0.S3.a).
 * - `reason` is the operator-supplied message; `status` is the termination
 *   status (`COMPLETED` / `FAILED` / custom).
 *
 * @typedef {Object} Terminate
 * @property {'terminate'} kind
 * @property {string} id
 * @property {string} label
 * @property {string} reason
 * @property {string} status
 * @property {350} w
 * @property {90} h
 * @property {false} hasIconBadge         Always false (Rule 0.S3.a)
 * @property {BBox} [bbox]
 * @property {PortMap} [portMap]
 */

/**
 * Workflow entry sentinel — 80×80 circle.
 *
 * @typedef {Object} Start
 * @property {'start'} kind
 * @property {string} id
 * @property {80} w
 * @property {80} h
 * @property {BBox} [bbox]
 * @property {PortMap} [portMap]
 */

/**
 * Workflow exit sentinel — 80×80 circle.
 *
 * @typedef {Object} End
 * @property {'end'} kind
 * @property {string} id
 * @property {80} w
 * @property {80} h
 * @property {BBox} [bbox]
 * @property {PortMap} [portMap]
 */

/**
 * The full discriminated union.
 *
 * @typedef {Leaf | Switch | Fork | DynamicFork | DoWhile | SubWorkflow | Terminate | Start | End} IRNode
 */

// ───────────────────────────────────────────────────────────────────
//  2. Constants (invariant field values)
// ───────────────────────────────────────────────────────────────────

/** Canonical head/shape dimensions by IR_KIND. Every constructor stamps these. */
export const IR_DIMS = Object.freeze({
  LEAF_W:         350,  // Rule 0.SA.a
  TERMINATE_W:    350,
  TERMINATE_H:    90,   // Rule 0.S3.a
  SWITCH_HEAD_W:  450,  // Rule 0.S2.a
  SWITCH_HEAD_H:  200,
  FORK_HEAD_W:    350,  // Rule 0.SB.a
  FORK_HEAD_H:    120,
  DOWHILE_HEAD_W: 350,
  DOWHILE_HEAD_H: 130,
  SUBWF_HEAD_W:   350,
  SUBWF_HEAD_H:   130,
  SENTINEL_W:     80,   // Rule 0.S0.a — start/end circle
  SENTINEL_H:     80,
});

// ───────────────────────────────────────────────────────────────────
//  3. Constructors (stamp invariants, check required fields)
// ───────────────────────────────────────────────────────────────────

/**
 * Construct a Leaf. Throws on missing required fields.
 *
 * @param {Object} p
 * @param {string} p.id
 * @param {string} p.label
 * @param {string} p.canvasType           Canonical CANVAS_TYPE leaf value
 * @param {Props} [p.props]
 * @param {number} [p.measuredH]
 * @returns {Leaf}
 */
export function makeLeaf({ id, label, canvasType, props = {}, measuredH }) {
  requireStr('Leaf.id', id);
  requireStr('Leaf.label', label);
  requireStr('Leaf.canvasType', canvasType);
  return {
    kind: IR_KIND.LEAF,
    id,
    label,
    canvasType,
    props,
    w: IR_DIMS.LEAF_W,
    ...(measuredH != null ? { measuredH } : {}),
  };
}

/**
 * Construct a Case (SWITCH arm). Not an IRNode — a sub-shape.
 *
 * @param {Object} p
 * @param {string} p.name
 * @param {boolean} [p.isDefault]
 * @param {IRNode[]} [p.children]
 * @returns {Case}
 */
export function makeCase({ name, isDefault = false, children = [] }) {
  requireStr('Case.name', name);
  if (!Array.isArray(children)) throw typeErr('Case.children', 'array');
  return { name, isDefault: !!isDefault, children };
}

/**
 * Construct a Branch (FORK arm). Not an IRNode — a sub-shape.
 *
 * @param {Object} p
 * @param {string} p.name
 * @param {IRNode[]} [p.children]
 * @returns {Branch}
 */
export function makeBranch({ name, children = [] }) {
  requireStr('Branch.name', name);
  if (!Array.isArray(children)) throw typeErr('Branch.children', 'array');
  return { name, children };
}

/**
 * Construct a Join. Not an IRNode — child of Fork / DynamicFork.
 *
 * @param {Object} p
 * @param {string} p.id
 * @param {string} p.label
 * @param {string[]} p.joinOn
 * @param {'ALL' | 'ANY'} [p.waitMode]
 * @returns {Join}
 */
export function makeJoin({ id, label, joinOn, waitMode = 'ALL' }) {
  requireStr('Join.id', id);
  requireStr('Join.label', label);
  if (!Array.isArray(joinOn)) throw typeErr('Join.joinOn', 'array');
  if (waitMode !== 'ALL' && waitMode !== 'ANY') {
    throw new Error(`Join.waitMode must be 'ALL' or 'ANY' (got '${waitMode}')`);
  }
  return { id, label, joinOn: [...joinOn], waitMode };
}

/**
 * Construct a Switch.
 *
 * @param {Object} p
 * @param {string} p.id
 * @param {string} p.label
 * @param {string} p.expr
 * @param {Case[]} p.cases
 * @param {Case | null} [p.defaultCase]
 * @returns {Switch}
 */
export function makeSwitch({ id, label, expr, cases, defaultCase = null, canvasType = null }) {
  requireStr('Switch.id', id);
  requireStr('Switch.label', label);
  // `expr` is display-only metadata — the layout pipeline routes by
  // case names on outgoing edges, not by re-evaluating the expression.
  // Allow empty / null so a switch a user is still configuring (no
  // expression typed yet) doesn't crash the layout. Coerced to '' so
  // downstream consumers can still treat it as a string.
  if (expr != null && typeof expr !== 'string') {
    throw typeErr('Switch.expr', 'string');
  }
  const safeExpr = typeof expr === 'string' ? expr : '';
  if (!Array.isArray(cases)) throw typeErr('Switch.cases', 'array');
  if (defaultCase != null && (typeof defaultCase !== 'object' || defaultCase.isDefault !== true)) {
    throw new Error('Switch.defaultCase must be a Case with isDefault=true, or null');
  }
  // `canvasType` is the originating canvas node type, carried so the
  // renderer can draw the container with the node's own chrome.
  //
  // A Switch is the IR for "exactly one arm runs", and a condition node is
  // not the only thing that shape describes: a policy gate, a DPG scan, an
  // approval and an RBAC check all pick one arm from a verdict rather than
  // from an expression. They render as a Switch, and without this they
  // would all draw as a condition diamond labelled with an empty
  // expression — correct branches, wrong node.
  //
  // Null for a real condition node, which is what the emitter already
  // assumes, so nothing that renders today changes.
  if (canvasType != null && typeof canvasType !== 'string') {
    throw typeErr('Switch.canvasType', 'string');
  }
  return {
    kind: IR_KIND.SWITCH,
    id,
    label,
    expr: safeExpr,
    cases: [...cases],
    defaultCase,
    canvasType: canvasType || null,
    convergence: null,
    headW: IR_DIMS.SWITCH_HEAD_W,
    headH: IR_DIMS.SWITCH_HEAD_H,
  };
}

/**
 * Construct a static Fork.
 *
 * @param {Object} p
 * @param {string} p.id
 * @param {string} p.label
 * @param {Branch[]} p.branches
 * @param {Join} p.join
 * @returns {Fork}
 */
export function makeFork({ id, label, branches, join }) {
  requireStr('Fork.id', id);
  requireStr('Fork.label', label);
  if (!Array.isArray(branches)) throw typeErr('Fork.branches', 'array');
  if (!join || typeof join !== 'object') throw typeErr('Fork.join', 'Join');
  return {
    kind: IR_KIND.FORK,
    id,
    label,
    branches: [...branches],
    join,
    headW: IR_DIMS.FORK_HEAD_W,
    headH: IR_DIMS.FORK_HEAD_H,
  };
}

/**
 * Construct a dynamic Fork (FORK_JOIN_DYNAMIC).
 *
 * @param {Object} p
 * @param {string} p.id
 * @param {string} p.label
 * @param {Branch[] | null} [p.resolvedBranches]  Null at design time
 * @param {Join} p.join
 * @returns {DynamicFork}
 */
export function makeDynamicFork({ id, label, resolvedBranches = null, join }) {
  requireStr('DynamicFork.id', id);
  requireStr('DynamicFork.label', label);
  if (resolvedBranches != null && !Array.isArray(resolvedBranches)) {
    throw typeErr('DynamicFork.resolvedBranches', 'array or null');
  }
  if (!join || typeof join !== 'object') throw typeErr('DynamicFork.join', 'Join');
  return {
    kind: IR_KIND.DYNAMIC_FORK,
    id,
    label,
    resolvedBranches: resolvedBranches ? [...resolvedBranches] : null,
    join,
    headW: IR_DIMS.FORK_HEAD_W,
    headH: IR_DIMS.FORK_HEAD_H,
  };
}

/**
 * Construct a DoWhile / FOR_EACH.
 *
 * @param {Object} p
 * @param {string} p.id
 * @param {string} p.label
 * @param {IRNode[]} p.body
 * @param {string} p.condition
 * @param {string} [p.iterationHint]
 * @returns {DoWhile}
 */
export function makeDoWhile({ id, label, body, condition, iterationHint }) {
  requireStr('DoWhile.id', id);
  requireStr('DoWhile.label', label);
  requireStr('DoWhile.condition', condition);
  if (!Array.isArray(body)) throw typeErr('DoWhile.body', 'array');
  return {
    kind: IR_KIND.DO_WHILE,
    id,
    label,
    body: [...body],
    condition,
    ...(iterationHint ? { iterationHint } : {}),
    headW: IR_DIMS.DOWHILE_HEAD_W,
    headH: IR_DIMS.DOWHILE_HEAD_H,
  };
}

/**
 * Construct a SubWorkflow (or async START_WORKFLOW when `async=true`).
 *
 * @param {Object} p
 * @param {string} p.id
 * @param {string} p.label
 * @param {string} p.refName
 * @param {number} [p.refVersion]
 * @param {boolean} [p.async]                 True ⇒ START_WORKFLOW
 * @param {IRNode | null} [p.embedded]
 * @returns {SubWorkflow}
 */
export function makeSubWorkflow({ id, label, refName, refVersion, async = false, embedded = null }) {
  requireStr('SubWorkflow.id', id);
  requireStr('SubWorkflow.label', label);
  requireStr('SubWorkflow.refName', refName);
  if (refVersion != null && typeof refVersion !== 'number') {
    throw typeErr('SubWorkflow.refVersion', 'number or undefined');
  }
  return {
    kind: IR_KIND.SUB_WORKFLOW,
    id,
    label,
    refName,
    ...(refVersion != null ? { refVersion } : {}),
    async: !!async,
    embedded,
    headW: IR_DIMS.SUBWF_HEAD_W,
    headH: IR_DIMS.SUBWF_HEAD_H,
  };
}

/**
 * Construct a Terminate.
 *
 * @param {Object} p
 * @param {string} p.id
 * @param {string} p.label
 * @param {string} p.reason
 * @param {string} p.status
 * @returns {Terminate}
 */
export function makeTerminate({ id, label, reason, status }) {
  requireStr('Terminate.id', id);
  requireStr('Terminate.label', label);
  requireStr('Terminate.reason', reason);
  requireStr('Terminate.status', status);
  return {
    kind: IR_KIND.TERMINATE,
    id,
    label,
    reason,
    status,
    w: IR_DIMS.TERMINATE_W,
    h: IR_DIMS.TERMINATE_H,
    hasIconBadge: false,
  };
}

/**
 * Construct a Start sentinel.
 *
 * @param {Object} [p]
 * @param {string} [p.id]
 * @returns {Start}
 */
export function makeStart({ id = 'start' } = {}) {
  requireStr('Start.id', id);
  return {
    kind: IR_KIND.START,
    id,
    w: IR_DIMS.SENTINEL_W,
    h: IR_DIMS.SENTINEL_H,
  };
}

/**
 * Construct an End sentinel.
 *
 * @param {Object} [p]
 * @param {string} [p.id]
 * @returns {End}
 */
export function makeEnd({ id = 'end' } = {}) {
  requireStr('End.id', id);
  return {
    kind: IR_KIND.END,
    id,
    w: IR_DIMS.SENTINEL_W,
    h: IR_DIMS.SENTINEL_H,
  };
}

// ───────────────────────────────────────────────────────────────────
//  4. Runtime validator — assertValidIR(node)
// ───────────────────────────────────────────────────────────────────

/**
 * Walk an IR tree and throw on the first schema violation. Intended for
 * dev-mode and unit tests; prod paths that have already gone through a
 * constructor do not need to re-run this (the constructors enforce the
 * same invariants).
 *
 * @param {IRNode} node
 * @param {string} [pathLabel]  Internal: the dotted path to the current node
 * @throws {Error} on any violation
 */
export function assertValidIR(node, pathLabel = 'root') {
  if (node == null || typeof node !== 'object') {
    throw new Error(`${pathLabel}: expected IRNode object, got ${node === null ? 'null' : typeof node}`);
  }
  const k = node.kind;
  if (!ALL_IR_KINDS.includes(k)) {
    throw new Error(`${pathLabel}: unknown IR kind '${k}'`);
  }
  requireStr(`${pathLabel}.id`, node.id);

  switch (k) {
    case IR_KIND.LEAF:
      requireStr(`${pathLabel}.label`, node.label);
      requireStr(`${pathLabel}.canvasType`, node.canvasType);
      requireObj(`${pathLabel}.props`, node.props);
      requireEq(`${pathLabel}.w`, node.w, IR_DIMS.LEAF_W);
      break;

    case IR_KIND.SWITCH:
      requireStr(`${pathLabel}.label`, node.label);
      // expr is display-only and may be empty for in-progress switches
      // (matches the relaxation in `makeSwitch`).
      if (node.expr != null && typeof node.expr !== 'string') {
        throw typeErr(`${pathLabel}.expr`, 'string');
      }
      if (!Array.isArray(node.cases)) throw typeErr(`${pathLabel}.cases`, 'array');
      node.cases.forEach((c, i) => assertValidCase(c, `${pathLabel}.cases[${i}]`, /*mustBeDefault*/ false));
      if (node.defaultCase != null) assertValidCase(node.defaultCase, `${pathLabel}.defaultCase`, /*mustBeDefault*/ true);
      // convergence may be null (common at construction) or a Convergence
      if (node.convergence != null) assertValidConvergence(node.convergence, `${pathLabel}.convergence`);
      requireEq(`${pathLabel}.headW`, node.headW, IR_DIMS.SWITCH_HEAD_W);
      requireEq(`${pathLabel}.headH`, node.headH, IR_DIMS.SWITCH_HEAD_H);
      break;

    case IR_KIND.FORK:
      requireStr(`${pathLabel}.label`, node.label);
      if (!Array.isArray(node.branches)) throw typeErr(`${pathLabel}.branches`, 'array');
      node.branches.forEach((b, i) => assertValidBranch(b, `${pathLabel}.branches[${i}]`));
      assertValidJoin(node.join, `${pathLabel}.join`);
      requireEq(`${pathLabel}.headW`, node.headW, IR_DIMS.FORK_HEAD_W);
      requireEq(`${pathLabel}.headH`, node.headH, IR_DIMS.FORK_HEAD_H);
      break;

    case IR_KIND.DYNAMIC_FORK:
      requireStr(`${pathLabel}.label`, node.label);
      if (node.resolvedBranches != null) {
        if (!Array.isArray(node.resolvedBranches)) throw typeErr(`${pathLabel}.resolvedBranches`, 'array or null');
        node.resolvedBranches.forEach((b, i) => assertValidBranch(b, `${pathLabel}.resolvedBranches[${i}]`));
      }
      assertValidJoin(node.join, `${pathLabel}.join`);
      requireEq(`${pathLabel}.headW`, node.headW, IR_DIMS.FORK_HEAD_W);
      requireEq(`${pathLabel}.headH`, node.headH, IR_DIMS.FORK_HEAD_H);
      break;

    case IR_KIND.DO_WHILE:
      requireStr(`${pathLabel}.label`, node.label);
      requireStr(`${pathLabel}.condition`, node.condition);
      if (!Array.isArray(node.body)) throw typeErr(`${pathLabel}.body`, 'array');
      node.body.forEach((n, i) => assertValidIR(n, `${pathLabel}.body[${i}]`));
      requireEq(`${pathLabel}.headW`, node.headW, IR_DIMS.DOWHILE_HEAD_W);
      requireEq(`${pathLabel}.headH`, node.headH, IR_DIMS.DOWHILE_HEAD_H);
      break;

    case IR_KIND.SUB_WORKFLOW:
      requireStr(`${pathLabel}.label`, node.label);
      requireStr(`${pathLabel}.refName`, node.refName);
      if (typeof node.async !== 'boolean') throw typeErr(`${pathLabel}.async`, 'boolean');
      if (node.embedded != null) assertValidIR(node.embedded, `${pathLabel}.embedded`);
      requireEq(`${pathLabel}.headW`, node.headW, IR_DIMS.SUBWF_HEAD_W);
      requireEq(`${pathLabel}.headH`, node.headH, IR_DIMS.SUBWF_HEAD_H);
      break;

    case IR_KIND.TERMINATE:
      requireStr(`${pathLabel}.label`, node.label);
      requireStr(`${pathLabel}.reason`, node.reason);
      requireStr(`${pathLabel}.status`, node.status);
      requireEq(`${pathLabel}.w`, node.w, IR_DIMS.TERMINATE_W);
      requireEq(`${pathLabel}.h`, node.h, IR_DIMS.TERMINATE_H);
      requireEq(`${pathLabel}.hasIconBadge`, node.hasIconBadge, false);
      break;

    case IR_KIND.START:
    case IR_KIND.END:
      requireEq(`${pathLabel}.w`, node.w, IR_DIMS.SENTINEL_W);
      requireEq(`${pathLabel}.h`, node.h, IR_DIMS.SENTINEL_H);
      break;

    default:
      // Exhaustiveness fallback — unreachable if ALL_IR_KINDS check passed above.
      throw new Error(`${pathLabel}: unhandled kind in switch '${k}'`);
  }
}

function assertValidCase(c, pathLabel, mustBeDefault) {
  requireObj(pathLabel, c);
  requireStr(`${pathLabel}.name`, c.name);
  if (typeof c.isDefault !== 'boolean') throw typeErr(`${pathLabel}.isDefault`, 'boolean');
  if (mustBeDefault && c.isDefault !== true) {
    throw new Error(`${pathLabel}.isDefault must be true (this is a defaultCase slot)`);
  }
  if (!Array.isArray(c.children)) throw typeErr(`${pathLabel}.children`, 'array');
  c.children.forEach((n, i) => assertValidIR(n, `${pathLabel}.children[${i}]`));
}

function assertValidBranch(b, pathLabel) {
  requireObj(pathLabel, b);
  requireStr(`${pathLabel}.name`, b.name);
  if (!Array.isArray(b.children)) throw typeErr(`${pathLabel}.children`, 'array');
  b.children.forEach((n, i) => assertValidIR(n, `${pathLabel}.children[${i}]`));
}

function assertValidJoin(j, pathLabel) {
  requireObj(pathLabel, j);
  requireStr(`${pathLabel}.id`, j.id);
  requireStr(`${pathLabel}.label`, j.label);
  if (!Array.isArray(j.joinOn)) throw typeErr(`${pathLabel}.joinOn`, 'array');
  if (j.waitMode !== undefined && j.waitMode !== 'ALL' && j.waitMode !== 'ANY') {
    throw new Error(`${pathLabel}.waitMode must be 'ALL' or 'ANY' (got '${j.waitMode}')`);
  }
}

function assertValidConvergence(c, pathLabel) {
  requireObj(pathLabel, c);
  requireStr(`${pathLabel}.id`, c.id);
  if (c.kind !== 'pill' && c.kind !== 'label_marker') {
    throw new Error(`${pathLabel}.kind must be 'pill' or 'label_marker' (got '${c.kind}')`);
  }
  if (!Array.isArray(c.livingBranchRefs)) throw typeErr(`${pathLabel}.livingBranchRefs`, 'array');
}

// ───────────────────────────────────────────────────────────────────
//  5. Shape helpers (tiny internal assertion primitives)
// ───────────────────────────────────────────────────────────────────

function requireStr(fieldPath, v) {
  if (typeof v !== 'string' || v.length === 0) {
    throw typeErr(fieldPath, 'non-empty string');
  }
}

function requireObj(fieldPath, v) {
  if (v == null || typeof v !== 'object' || Array.isArray(v)) {
    throw typeErr(fieldPath, 'object');
  }
}

function requireEq(fieldPath, actual, expected) {
  if (actual !== expected) {
    throw new Error(`${fieldPath} must be ${JSON.stringify(expected)} (got ${JSON.stringify(actual)})`);
  }
}

function typeErr(fieldPath, expected) {
  return new Error(`${fieldPath} must be ${expected}`);
}

// ───────────────────────────────────────────────────────────────────
//  6. Convenience: tree walker (in-order, parent-first)
// ───────────────────────────────────────────────────────────────────

/**
 * Yield every IRNode in the tree, parent-first, depth-first.
 * Used by dev-mode validators, id-uniqueness checks, and debug traversals.
 *
 * @param {IRNode} root
 * @returns {Generator<IRNode, void, unknown>}
 */
export function* walkIR(root) {
  yield root;
  switch (root.kind) {
    case IR_KIND.SWITCH: {
      for (const c of root.cases) {
        for (const n of c.children) yield* walkIR(n);
      }
      if (root.defaultCase) {
        for (const n of root.defaultCase.children) yield* walkIR(n);
      }
      break;
    }
    case IR_KIND.FORK: {
      for (const b of root.branches) {
        for (const n of b.children) yield* walkIR(n);
      }
      break;
    }
    case IR_KIND.DYNAMIC_FORK: {
      if (root.resolvedBranches) {
        for (const b of root.resolvedBranches) {
          for (const n of b.children) yield* walkIR(n);
        }
      }
      break;
    }
    case IR_KIND.DO_WHILE: {
      for (const n of root.body) yield* walkIR(n);
      break;
    }
    case IR_KIND.SUB_WORKFLOW: {
      if (root.embedded) yield* walkIR(root.embedded);
      break;
    }
    default:
      // Leaf, Terminate, Start, End — no children
      break;
  }
}

/** Re-export for callers that want the kind enum alongside the constructors. */
export { IR_KIND, CANVAS_TYPE } from './kinds';
